import React, { useCallback } from 'react';

import { ActionIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import useManualCreditAthleteAppointment from 'hooks/athleteAppointment/useManualCreditAthleteAppointment';
import useManualCreditAthleteClass from 'hooks/athleteClass/useManualCreditAthleteClass';
import type { AthleteEvent } from 'types';
import notificationHandler from 'utils/notifications';

type CreditActionIconProps = {
  athleteEvent: AthleteEvent;
};

const notificationMessages = {
  credit: {
    success: {
      title: 'Reservation has been credited',
      message: 'A credit has been assigned to this reservation.',
    },
    error: {
      title: "Reservation can't be credited.",
    },
  },
  uncredit: {
    success: {
      title: 'Reservation has been uncredited',
      message: 'A credit has been removed from an existing reservation. You can now re-assign this credit.',
    },
    error: {
      title: "Reservation can't be uncredited.",
    },
  },
};

function useHandleManualCredit(): (athleteEvent: AthleteEvent, action: 'credit' | 'uncredit') => void {
  const manualCreditAthleteClass = useManualCreditAthleteClass();
  const manualCreditAthleteAppointment = useManualCreditAthleteAppointment();

  const { successNotification, errorNotification } = notificationHandler();

  const handleManualCredit = useCallback(
    (athleteEvent: AthleteEvent, action: 'credit' | 'uncredit'): void => {
      const { type, athleteEventId } = athleteEvent;

      if (type === 'athlete_kilo_class') {
        manualCreditAthleteClass.mutate(
          {
            athleteKiloClassId: athleteEventId,
            manualCredit: { action },
          },
          {
            onSuccess: () => {
              successNotification(
                notificationMessages[action].success.title,
                notificationMessages[action].success.message,
              );
            },
            onError: error => {
              errorNotification(notificationMessages[action].error.title, error.response?.data.message);
            },
          },
        );
      }

      if (type === 'athlete_appointment') {
        manualCreditAthleteAppointment.mutate(
          {
            athleteAppointmentId: athleteEventId,
            manualCredit: { action },
          },
          {
            onSuccess: () => {
              successNotification(
                notificationMessages[action].success.title,
                notificationMessages[action].success.message,
              );
            },
            onError: error => {
              errorNotification(notificationMessages[action].error.title, error.response?.data.message);
            },
          },
        );
      }
    },
    [manualCreditAthleteAppointment, manualCreditAthleteClass, errorNotification, successNotification],
  );

  return handleManualCredit;
}

const CreditActionIcon = ({ athleteEvent }: CreditActionIconProps): JSX.Element => {
  const theme = useMantineTheme();
  const tooltipMessage = athleteEvent.isCredited ? 'Remove credit' : 'Assign credit';

  const handleManualCredit = useHandleManualCredit();

  return (
    <Tooltip label={tooltipMessage}>
      {athleteEvent.isCredited ? (
        <ActionIcon
          variant='transparent'
          color='kiloBlue'
          size='lg'
          onClick={() => handleManualCredit(athleteEvent, 'uncredit')}
          disabled={!athleteEvent.canBeUncredited}
        >
          <IconCircleCheckFilled color={athleteEvent.canBeUncredited ? 'kiloBlue' : 'gray'} />
        </ActionIcon>
      ) : (
        <ActionIcon
          variant='transparent'
          color='gray'
          size='lg'
          onClick={() => handleManualCredit(athleteEvent, 'credit')}
          disabled={!athleteEvent.canBeCredited}
        >
          <IconCircleOff color={athleteEvent.canBeCredited ? 'gray' : theme.colors.gray[4]} />
        </ActionIcon>
      )}
    </Tooltip>
  );
};

export default CreditActionIcon;
